import type { FeatureConfig } from '@skynet/feature-config';
import { ApartmentOutlined } from '@ant-design/icons';
import { ROLE_DETAIL_ROUTE_PATH, ROLES_LIST_URL, ROLES_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Roles list and role detail pages. The dashboard shell reads
 * this single source of truth instead of hardcoding the route, navigation
 * entry, header label and Cerbos action across its routing, navigation,
 * header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to `@skynet/sky-role`'s
 * components. The config imports nothing from `@skynet/sky-role`, so reading it
 * never drags the Roles UI onto the boot path.
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:roles`), and renaming any
 * of them hides the nav item until the policy is renamed too.
 */
export const rolesConfig = {
  domain: 'Admin',
  section: 'roles',
  route: {
    path: ROLES_ROUTE_PATH,
    load: async () => ({ default: (await import('./Roles')).Roles }),
  },
  detailRoute: {
    path: ROLE_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./RolePage')).RolePage }),
  },
  nav: {
    key: 'admin.roles',
    label: 'Roles',
    icon: <ApartmentOutlined />,
    url: ROLES_LIST_URL,
  },
  header: { label: 'ROLES' },
  cerbosAction: 'read:admin:roles',
  skipWsLoginPaths: [ROLES_LIST_URL],
} as const satisfies FeatureConfig;
